import React from 'react';
import Layout from '../layouts/Layout';
import API, { PUBLIC_URL } from '../utils/const';
import { useTranslation } from 'react-i18next';
import { Head } from 'vite-react-ssg';
import { useLocation } from 'react-router-dom';
import { trackCtaClick } from '../utils/analytics';
import {
    Accordion,
    Anchor,
    Badge,
    Box,
    Button,
    Card,
    Center,
    Code,
    Container,
    CopyButton,
    Divider,
    Grid,
    Group,
    Paper,
    Stack,
    Text,
    ThemeIcon,
    Title,
} from '@mantine/core';
import {
    IconAdjustments,
    IconArrowsRightLeft,
    IconCalendarPlus,
    IconEyeOff,
} from '@tabler/icons-react';

const MCP_SERVER_URL = API
    ? `${API.replace(/\/$/, '')}/mcp`
    : 'https://api.calensync.live/mcp';

const CLAUDE_CONNECT_URL =
    `https://claude.ai/customize/connectors?modal=add-custom-connector`
    + `&connectorName=Calensync`
    + `&connectorUrl=${encodeURIComponent(MCP_SERVER_URL)}`;
const CHATGPT_CONNECT_URL = 'https://chatgpt.com/#settings/Connectors';
const PERPLEXITY_CONNECT_URL = 'https://www.perplexity.ai/account/connectors';

const ACCORDION_CONTROL_STYLES = {
    label: {
        fontSize: 'var(--mantine-font-size-lg)',
        fontWeight: '500',
    },
} as const;

const CTAButtonProps = {
    color: 'primary.4' as const,
    radius: 'md' as const,
    fw: 300,
    px: 'xl' as const,
    size: 'compact-xl' as const,
};

const CLAUDE_LOGO_PATH =
    'm4.7144 15.9555 4.7174-2.6471.079-.2307-.079-.1275h-.2307l-.7893-.0486-2.6956-.0729-2.3375-.0971-2.2646-.1214-.5707-.1215-.5343-.7042.0546-.3522.4797-.3218.686.0608 1.5179.1032 2.2767.1578 1.6514.0972 2.4468.255h.3886l.0546-.1579-.1336-.0971-.1032-.0972L6.973 9.8356l-2.55-1.6879-1.3356-.9714-.7225-.4918-.3643-.4614-.1578-1.0078.6557-.7225.8803.0607.2246.0607.8925.686 1.9064 1.4754 2.4893 1.8336.3643.3035.1457-.1032.0182-.0728-.164-.2733-1.3539-2.4467-1.445-2.4893-.6435-1.032-.17-.6194c-.0607-.255-.1032-.4674-.1032-.7285L6.287.1335 6.6997 0l.9957.1336.419.3642.6192 1.4147 1.0018 2.2282 1.5543 3.0296.4553.8985.2429.8318.091.255h.1579v-.1457l.1275-1.706.2368-2.0947.2307-2.6957.0789-.7589.3764-.9107.7468-.4918.5828.2793.4797.686-.0668.4433-.2853 1.8517-.5586 2.9021-.3643 1.9429h.2125l.2429-.2429.9835-1.3053 1.6514-2.0643.7286-.8196.85-.9046.5464-.4311h1.0321l.759 1.1293-.34 1.1657-1.0625 1.3478-.8804 1.1414-1.2628 1.7-.7893 1.36.0729.1093.1882-.0183 2.8535-.607 1.5421-.2794 1.8396-.3157.8318.3886.091.3946-.3278.8075-1.967.4857-2.3072.4614-3.4364.8136-.0425.0304.0486.0607 1.5482.1457.6618.0364h1.621l3.0175.2247.7892.522.4736.6376-.079.4857-1.2142.6193-1.6393-.3886-3.825-.9107-1.3113-.3279h-.1822v.1093l1.0929 1.0686 2.0035 1.8092 2.5075 2.3314.1275.5768-.3218.4554-.34-.0486-2.2039-1.6575-.85-.7468-1.9246-1.621h-.1275v.17l.4432.6496 2.3436 3.5214.1214 1.0807-.17.3521-.6071.2125-.6679-.1214-1.3721-1.9246L14.38 17.959l-1.1414-1.9428-.1397.079-.674 7.2552-.3156.3703-.7286.2793-.6071-.4614-.3218-.7468.3218-1.4753.3886-1.9246.3157-1.53.2853-1.9004.17-.6314-.0121-.0425-.1397.0182-1.4328 1.9672-2.1796 2.9446-1.7243 1.8456-.4128.164-.7164-.3704.0667-.6618.4008-.5889 2.386-3.0357 1.4389-1.882.929-1.0868-.0062-.1579h-.0546l-6.3385 4.1164-1.1293.1457-.4857-.4554.0608-.7467.2307-.2429 1.9064-1.3114Z';

const OPENAI_LOGO_PATH =
    'M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z';

const PERPLEXITY_LOGO_PATH =
    'M22.3977 7.0896h-2.3106V.0676l-7.5094 6.3542V.1577h-1.1554v6.1966L4.4904 0v7.0896H1.6023v10.3976h2.8882V24l6.932-6.3591v6.2005h1.1554v-6.0469l6.9318 6.1807v-6.4879h2.8882V7.0896zm-3.4657-4.531v4.531h-5.355l5.355-4.531zm-13.2862.0676 4.8691 4.4634H5.6458V2.6262zM2.7576 16.332V8.245h7.8476l-6.1149 6.1147v1.9723H2.7576zm2.8882 5.0404v-3.8852h.0001v-2.6488l5.7763-5.7764v7.0111l-5.7764 5.2993zm12.7086.0248-5.7766-5.1509V9.0618l5.7766 5.7766v6.5588zm2.8882-5.0652h-1.733v-1.9723L13.3948 8.245h7.8478v8.087z';

function BrandMark({
    path,
    size = 22,
    color = 'currentColor',
}: {
    path: string;
    size?: number;
    color?: string;
}) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
            <path fill={color} d={path} />
        </svg>
    );
}

const CONNECT_CLIENTS = [
    { name: 'claude', href: CLAUDE_CONNECT_URL, path: CLAUDE_LOGO_PATH, brandColor: '#D97757' },
    { name: 'chatgpt', href: CHATGPT_CONNECT_URL, path: OPENAI_LOGO_PATH, brandColor: '#10A37F' },
    { name: 'perplexity', href: PERPLEXITY_CONNECT_URL, path: PERPLEXITY_LOGO_PATH, brandColor: '#1B8A7A' },
] as const;

const ConnectButtons: React.FC<{ location: string }> = ({ location }) => {
    const { t } = useTranslation();

    const buttons = CONNECT_CLIENTS.map((client) => (
        <Button
            key={client.name}
            {...CTAButtonProps}
            c="white"
            component="a"
            href={client.href}
            target="_blank"
            rel="noopener noreferrer"
            leftSection={<BrandMark path={client.path} size={18} color="white" />}
            onClick={() => trackCtaClick('mcp', `${location}_${client.name}`)}
        >
            {t(`mcp_page.connect.${client.name}`)}
        </Button>
    ));

    return (
        <Group justify="center" gap="sm" wrap="wrap">
            {buttons}
        </Group>
    );
};

const McpUrlCard: React.FC = () => {
    const { t } = useTranslation();
    return (
        <Card withBorder radius="md" p="md" maw={640} mx="auto">
            <Stack gap="sm" align="center">
                <Text size="sm" c="dimmed" ta="center">{t('mcp_page.url.label')}</Text>
                <Code block style={{ width: '100%', wordBreak: 'break-all' }}>
                    {MCP_SERVER_URL}
                </Code>
                <CopyButton value={MCP_SERVER_URL}>
                    {({ copied, copy }) => (
                        <Button variant="light" color="primary.4" onClick={copy}>
                            {copied ? t('mcp_page.url.copied') : t('mcp_page.url.copy')}
                        </Button>
                    )}
                </CopyButton>
            </Stack>
        </Card>
    );
};

const InstructionSteps: React.FC<{ prefix: string }> = ({ prefix }) => {
    const { t } = useTranslation();
    return (
        <Stack gap="sm">
            {[1, 2, 3, 4, 5].map((step) => (
                <React.Fragment key={step}>
                    <Text size="lg" fw={200} lh={1.4}>
                        <Text span fw={600}>{step}. </Text>
                        {t(`${prefix}.step${step}`)}
                    </Text>
                    {step === 3 && (
                        <Code block style={{ wordBreak: 'break-all' }}>{MCP_SERVER_URL}</Code>
                    )}
                </React.Fragment>
            ))}
        </Stack>
    );
};

const InnerMcp: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const capabilities = [
        { icon: IconCalendarPlus, key: 'connect' },
        { icon: IconArrowsRightLeft, key: 'rules' },
        { icon: IconEyeOff, key: 'privacy' },
        { icon: IconAdjustments, key: 'customize' },
    ] as const;

    return (
        <>
            <Head>
                <meta charSet="utf-8" />
                <title>{t('mcp_page.meta.og_title')}</title>
                <link
                    rel="canonical"
                    href={`https://calensync.live${location.pathname.replace('/en', '')}`}
                />
                <link rel="alternate" href={`https://calensync.live${PUBLIC_URL}/es/mcp`} hrefLang="es" />
                <link rel="alternate" href={`https://calensync.live${PUBLIC_URL}/fr/mcp`} hrefLang="fr" />
                <link rel="alternate" href={`https://calensync.live${PUBLIC_URL}/en/mcp`} hrefLang="en" />
                <link rel="alternate" href={`https://calensync.live${PUBLIC_URL}/it/mcp`} hrefLang="it" />
                <link rel="alternate" href={`https://calensync.live${PUBLIC_URL}/mcp`} hrefLang="x-default" />
                <meta name="description" content={t('mcp_page.meta.description')} />
                <meta property="og:title" content={t('mcp_page.meta.og_title')} />
                <meta property="og:description" content={t('mcp_page.meta.description')} />
            </Head>

            <Box className="hero">
                <Container size="lg" py="xl">
                    <Stack align="center" gap="lg" py={{ base: 'sm', md: 'xl' }} maw={820} mx="auto">
                        <Badge color="primary.4" size="lg" variant="filled" fw={400}>
                            {t('mcp_page.hero.badge')}
                        </Badge>
                        <Title order={1} ta="center">{t('mcp_page.hero.title')}</Title>
                        <Title order={2} fw={200} ta="center">
                            {t('mcp_page.hero.heading')}
                        </Title>
                        <ConnectButtons location="hero" />
                    </Stack>
                </Container>
            </Box>

            <Box bg="gray.0" py="xl">
                <Container size="lg" my="xl">
                    <Stack align="center" mb="xl">
                        <Title order={2} className="display-4" ta="center">
                            {t('mcp_page.capabilities.title')}
                        </Title>
                        <Text size="xl" fw={200} ta="center" maw={720} lh={1.4}>
                            {t('mcp_page.capabilities.intro')}
                        </Text>
                    </Stack>
                    <Grid gap={{ base: 'md', lg: 'xl' }}>
                        {capabilities.map(({ icon: Icon, key }) => (
                            <Grid.Col key={key} span={{ base: 12, sm: 6 }}>
                                <Stack gap="xs">
                                    <Group gap="sm">
                                        <ThemeIcon size="lg" radius="md" color="primary.4" variant="filled">
                                            <Icon size={20} color="white" />
                                        </ThemeIcon>
                                        <Title order={3} fw={500}>
                                            {t(`mcp_page.capabilities.${key}_title`)}
                                        </Title>
                                    </Group>
                                    <Text size="lg" fw={200} lh={1.3}>
                                        {t(`mcp_page.capabilities.${key}_body`)}
                                    </Text>
                                </Stack>
                            </Grid.Col>
                        ))}
                    </Grid>
                </Container>
            </Box>

            <Box className="hero" py="xl">
                <Container size="lg" my="xl">
                    <Stack align="center" mb="xl">
                        <Title order={2} className="display-4" ta="center">
                            {t('mcp_page.connects.title')}
                        </Title>
                        <Text size="xl" fw={200} ta="center" maw={720} lh={1.4}>
                            {t('mcp_page.connects.intro')}
                        </Text>
                    </Stack>
                    <Grid>
                        {CONNECT_CLIENTS.map((client) => (
                            <Grid.Col key={client.name} span={{ base: 12, sm: 4 }}>
                                <Card withBorder radius="md" p="lg" h="100%" bg="white">
                                    <Stack align="center" gap="sm">
                                        <Paper
                                            withBorder
                                            radius="md"
                                            bg="white"
                                            w={56}
                                            h={56}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <BrandMark
                                                path={client.path}
                                                size={28}
                                                color={client.brandColor}
                                            />
                                        </Paper>
                                        <Title order={3} fw={500}>
                                            {t(`mcp_page.instructions.${client.name}.title`)}
                                        </Title>
                                        <Anchor href="#setup" size="sm">
                                            {t('mcp_page.connects.learn_how')}
                                        </Anchor>
                                    </Stack>
                                </Card>
                            </Grid.Col>
                        ))}
                    </Grid>
                </Container>
            </Box>

            <Box bg="gray.0" py="xl" id="setup">
                <Container size="lg" my="xl">
                    <Stack align="center" mb="xl">
                        <Title order={2} className="display-4" ta="center">
                            {t('mcp_page.started.title')}
                        </Title>
                    </Stack>
                    <Grid mb="xl">
                        {[1, 2, 3].map((step) => (
                            <Grid.Col key={step} span={{ base: 12, md: 4 }}>
                                <Stack gap="sm">
                                    <ThemeIcon size={42} radius="xl" color="primary.4" variant="filled">
                                        <Text fw={600} c="white">{step}</Text>
                                    </ThemeIcon>
                                    <Title order={3} fw={500}>
                                        {t(`mcp_page.started.step${step}_title`)}
                                    </Title>
                                    <Text size="lg" fw={200} lh={1.3}>
                                        {t(`mcp_page.started.step${step}_body`)}
                                    </Text>
                                </Stack>
                            </Grid.Col>
                        ))}
                    </Grid>
                    <Center mb="xl">
                        <ConnectButtons location="setup" />
                    </Center>
                    <McpUrlCard />
                </Container>
            </Box>

            <Box bg="primary.0" py="xl">
                <Container size="lg" py="md">
                    <Stack align="center" mb="xl">
                        <Title order={2} className="display-4" ta="center">
                            {t('mcp_page.instructions.title')}
                        </Title>
                        <Text size="xl" fw={200} ta="center" maw={720} lh={1.4}>
                            {t('mcp_page.instructions.intro')}
                        </Text>
                    </Stack>
                    <Accordion variant="contained" color="primary.4">
                        {(['claude', 'chatgpt', 'perplexity'] as const).map((client) => (
                            <div key={client}>
                                <Accordion.Item value={client}>
                                    <Accordion.Control
                                        py="xs"
                                        styles={ACCORDION_CONTROL_STYLES}
                                    >
                                        {t(`mcp_page.instructions.${client}.title`)}
                                    </Accordion.Control>
                                    <Accordion.Panel>
                                        <InstructionSteps prefix={`mcp_page.instructions.${client}`} />
                                    </Accordion.Panel>
                                </Accordion.Item>
                                <Divider color="gray.5" />
                            </div>
                        ))}
                        <Accordion.Item value="other">
                            <Accordion.Control py="xs" styles={ACCORDION_CONTROL_STYLES}>
                                {t('mcp_page.instructions.other.title')}
                            </Accordion.Control>
                            <Accordion.Panel>
                                <Stack gap="sm">
                                    <Text size="lg" fw={200} lh={1.4}>
                                        {t('mcp_page.instructions.other.intro')}
                                    </Text>
                                    <Text size="lg" fw={200} lh={1.4}>
                                        {t('mcp_page.instructions.other.cursor')}
                                    </Text>
                                    <Text size="lg" fw={200} lh={1.4}>
                                        {t('mcp_page.instructions.other.claude_code')}
                                    </Text>
                                    <Code block>
                                        {`claude mcp add --transport http calensync ${MCP_SERVER_URL}`}
                                    </Code>
                                    <Text size="lg" fw={200} lh={1.4}>
                                        {t('mcp_page.instructions.other.generic')}
                                    </Text>
                                    <Code block style={{ wordBreak: 'break-all' }}>{MCP_SERVER_URL}</Code>
                                </Stack>
                            </Accordion.Panel>
                        </Accordion.Item>
                    </Accordion>
                </Container>
            </Box>

            <Box bg="gray.0" py="xl">
                <Container size="lg" py="md">
                    <Stack align="center" mb="xl">
                        <Title order={2} className="display-4">F.A.Q.</Title>
                    </Stack>
                    <Accordion variant="contained" color="primary.4">
                        {['1', '2', '3', '4', '5', '6', '7'].map((idx) => (
                            <div key={idx}>
                                <Accordion.Item value={idx}>
                                    <Accordion.Control
                                        py="xs"
                                        styles={ACCORDION_CONTROL_STYLES}
                                    >
                                        {t(`mcp_page.faq.q${idx}`)}
                                    </Accordion.Control>
                                    <Accordion.Panel>
                                        <Text size="lg" fw={200}>{t(`mcp_page.faq.a${idx}`)}</Text>
                                    </Accordion.Panel>
                                </Accordion.Item>
                                <Divider color="gray.5" />
                            </div>
                        ))}
                    </Accordion>
                </Container>
            </Box>

            <Box className="hero" py="xl">
                <Container size="lg" py="lg">
                    <Stack align="center" gap="md">
                        <Title order={2} className="display-4" ta="center">
                            {t('mcp_page.cta.title')}
                        </Title>
                        <Text size="lg" ta="center" maw={640}>
                            {t('mcp_page.cta.body')}
                        </Text>
                        <ConnectButtons location="footer" />
                    </Stack>
                </Container>
            </Box>
        </>
    );
};

const Mcp: React.FC = () => (
    <Layout>
        <InnerMcp />
    </Layout>
);

export default Mcp;
